<template>
    <section class="doc-section">

        <!-- Selector de Plataforma -->
        <div class="mb-6">
            <BotonesNavegacion @cambio-plataforma="plataformaActual = $event" />
        </div>

        <header class="header-container">
            <h1>Eliminar Geocerca</h1>
            <p class="description">
                Este proceso permite remover de forma definitiva una geocerca registrada en el sistema desde el listado principal.
            </p>
        </header>

        <div class="two-col">
            <div class="col-steps">
                <p class="steps-label">Flujo de eliminación</p>

                <div class="step-card">
                    <div class="step-badge">1</div>
                    <div>
                        <div class="step-title">Seleccionar acción</div>
                        <div class="step-desc">
                            En la tabla de geocercas, haga clic sobre el botón de <strong>Eliminar</strong> (icono de basurero).
                        </div>
                    </div>
                </div>

                <div class="step-card">
                    <div class="step-badge">2</div>
                    <div>
                        <div class="step-title">Confirmación</div>
                        <div class="step-desc">
                            El sistema mostrará una ventana emergente para evitar eliminaciones accidentales.
                        </div>
                    </div>
                </div>
            </div>

            <transition name="fade" mode="out-in">
                <CarruselAciqui
                    :key="plataformaActual"
                    :items="imagenesMostrar"
                />
            </transition>
        </div>

        <div class="section-divider">
            <h2>Opciones de confirmación</h2>
        </div>

        <div class="form-grid">
            <div class="field-row">
                <span class="field-key">A</span>
                <div>
                    <div class="field-label">Aceptar</div>
                    <div class="field-desc">Confirma la acción y elimina la geocerca permanentemente.</div>
                </div>
            </div>
            <div class="field-row">
                <span class="field-key">B</span>
                <div>
                    <div class="field-label">Cancelar</div>
                    <div class="field-desc">Cierra la ventana y mantiene el registro sin cambios.</div>
                </div>
            </div>
        </div>

        <footer class="success-section">
            <div class="section-divider">
                <h2>Resultado de la operación</h2>
            </div>
                <p>Al eliminar la geocerca exitosamente, el sistema actualizará la tabla automáticamente y mostrará esta notificación:</p>
                <div class="carousel-box success-carousel">
                <transition name="fade" mode="out-in">
                    <CarruselAciqui
                        :key="plataformaActual"
                        :items="imagenesExitoMostrar"
                    />
                </transition>            
            </div>
        </footer>

    </section>
</template>

<script setup>
import { ref, computed } from 'vue';
import CarruselAciqui from '../Carruselimg.vue';
import BotonesNavegacion from '../BotonesNavegacion.vue';

const plataformaActual = ref('web');

/* ── Datos Web ───────────────────────────────────── */
const webData = {
    confirmacion: [
        {
            nombre: "Ventana de confirmación",
            url: "/img/adminGeocercas/Confirmar_Eliminar.png",
            descripcion: "Referencia visual de la alerta de confirmación."
        }
    ],
    exito: [
        {
            nombre: "Notificación de éxito",
            url: "/img/adminGeocercas/Notificacion_Eliminar_Exito.png",
            descripcion: "Confirmación visual de eliminación exitosa."
        }
    ]
};

/* ── Datos Móvil ─────────────────────────────────── */
const mobileData = {
    confirmacion: [
        {
            nombre: "Ventana de confirmación",
            url: "/img/adminGeocercas/Eliminar_Geocerca_Movil.png",
            descripcion: "Referencia visual de la alerta de confirmación en móvil."
        }
    ],
    exito: [
        {
            nombre: "Notificación de éxito",
            url: "/img/adminGeocercas/Notificacion_Eliminar_Exito.png",
            descripcion: "Confirmación visual de eliminación exitosa."
        }
    ]
};

/* ── Computed ────────────────────────────────────── */
const imagenesMostrar = computed(() =>
    plataformaActual.value === 'web'
        ? webData.confirmacion
        : mobileData.confirmacion
);

const imagenesExitoMostrar = computed(() =>
    plataformaActual.value === 'web'
        ? webData.exito
        : mobileData.exito
);
</script>

<style scoped>
/* ── Estilos base unificados ──────────────────────── */
.doc-section {
    padding: 20px;
    background: var(--bg-color);
}

.description {
    font-size: 15px;
    opacity: 0.8;
    margin-bottom: 24px;
}

/* ── Layout dos columnas ──────────────────────────── */
.two-col {
    display: flex;
    gap: 24px;
    align-items: flex-start;
    margin-bottom: 28px;
}

.col-steps { flex: 1; }
.col-image  { flex: 1; position: sticky; top: 20px; }

/* ── Pasos (Cards) ────────────────────────────────── */
.steps-label {
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.07em;
    text-transform: uppercase;
    color: var(--primary-color);
    margin-bottom: 12px;
}

.step-card {
    display: flex;
    gap: 14px;
    align-items: flex-start;
    background: var(--code-bg);
    border: 1px solid var(--border-color);
    border-radius: 10px;
    padding: 14px 16px;
    margin-bottom: 10px;
    transition: all 0.2s ease;
}

.step-card:hover { border-color: var(--primary-color); }

.step-badge {
    min-width: 28px;
    height: 28px;
    border-radius: 50%;
    background: var(--primary-color);
    color: #ffffff;
    font-size: 13px;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.step-title {
    font-size: 14px;
    font-weight: 600;
    margin-bottom: 4px;
}

.step-desc {
    font-size: 13px;
    opacity: 0.8;
    line-height: 1.5;
}

/* ── Separadores ──────────────────────────────────── */
.section-divider {
    display: flex;
    align-items: center;
    gap: 12px;
    margin: 32px 0 16px;
}

.section-divider h2 {
    font-size: 20px;
    font-weight: 700;
    color: var(--primary-color); /* Aquí es donde toma el azul */
    margin: 0;
}

.divider-line { flex: 1; height: 1px; background: var(--border-color); }

/* ── Formulario Grid (para Aceptar/Cancelar) ──────── */
.form-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px;
    margin: 20px 0;
}

.field-row {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    background: var(--code-bg);
    border: 1px solid var(--border-color);
    border-radius: 8px;
    padding: 10px 12px;
}

.field-key {
    font-weight: 700;
    color: var(--primary-color);
    font-size: 14px;
}

.field-label { font-weight: 600; font-size: 13px; }
.field-desc { font-size: 12px; opacity: 0.6; }

/* ── Éxito ────────────────────────────────────────── */
.success-section {
    margin-top: 40px;
    text-align: center;
}

.success-section p {
    font-size: 14px;
    opacity: 0.75;
    margin-bottom: 15px;
}

.carousel-box {
    border: 1px solid var(--border-color);
    padding: 8px;
    border-radius: 10px;
    background: var(--code-bg);
}

.mb-6 {
    margin-bottom: 2rem;
}

/* ── Animación ───────────────────────────────────── */
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}

/* Contenedor centrado y con tamaño controlado para la notificación */
.success-carousel {
    max-width: 700px; /* Antes era 400px */
    margin: 12px auto 0;
}
.success-carousel :deep(.carrusel-aciqui),
.success-carousel :deep(.carrusel-wrapper),
.success-carousel :deep(.carousel-item),
.success-carousel :deep(.carousel-container) {
    width: 100% !important;
    max-width: none !important;
}

.success-carousel :deep(img) {
    width: 100% !important;
    height: auto !important;
    object-fit: contain;
    border-radius: 8px;
    display: block;
}

/* ── Responsive ───────────────────────────────────── */
@media (max-width: 768px) {
    .two-col, .form-grid { grid-template-columns: 1fr; flex-direction: column; }
}
</style>